// Creation helpers shared by Add sheet, the context menu and double-click shortcuts.
import {
  createShapeId,
  toRichText,
  type TLTextShape,
} from "@tldraw/tlschema";
import { startEditingShapeWithRichText, type Editor, type VecLike } from "./canvas";
import type { Sheet } from "../shared/schema";
import {
  blankSheetProps,
  sheetDimensionsMm,
  type SheetSizeChoice,
} from "../shared/sheets";
import { imagePlacement, orderedSheets } from "./sheet-operations";

export function createBlankSheet(
  editor: Editor,
  options: {
    name: string;
    paper: SheetSizeChoice;
    landscape: boolean;
    widthMm: number;
    heightMm: number;
    scale?: number;
    x: number;
    y: number;
  },
) {
  const id = createShapeId();
  const unitsPerMm = 8 / 3;
  editor.markHistoryStoppingPoint("Add blank sheet");
  editor.createShape<Sheet>({
    id,
    type: "sheet",
    parentId: editor.getCurrentPageId(),
    x: options.x,
    y: options.y,
    props: {
      ...blankSheetProps(
        options.name,
        options.paper === "Custom" ? "A3" : options.paper,
        options.landscape,
        options.scale ?? 0,
      ),
      w: options.widthMm * unitsPerMm,
      h: options.heightMm * unitsPerMm,
      paper: options.paper,
    },
    meta: {
      order: orderedSheets(editor).length,
      paperWidthMm: options.widthMm,
      paperHeightMm: options.heightMm,
    },
  });
  return id;
}

/** Double-clicking the toolbar's Page button adds an A3 landscape sheet, placed like Add sheet. */
export function quickAddSheet(editor: Editor) {
  const { widthMm, heightMm } = sheetDimensionsMm("A3", true);
  const bounds = editor.getCurrentPageBounds();
  const id = createBlankSheet(editor, {
    name: `Sheet ${orderedSheets(editor).length + 1}`,
    paper: "A3",
    landscape: true,
    widthMm,
    heightMm,
    x: bounds ? bounds.maxX + 120 : 0,
    y: 0,
  });
  return editor.getShape<Sheet>(id)!;
}

/** Start an empty text object at a page point, on the sheet under it when there is one. */
export function addTextAt(editor: Editor, point: VecLike) {
  const { parentId, center } = imagePlacement(editor, point);
  const id = createShapeId();
  editor.markHistoryStoppingPoint("Add text");
  editor.setCurrentTool("select");
  // Match the Text tool's interactive defaults (BoardTextUtil only applies
  // them while the text tool is active).
  editor.createShape<TLTextShape>({
    id,
    type: "text",
    parentId,
    x: center.x,
    y: center.y,
    meta: { calqueTextSize: 12 },
    props: {
      richText: toRichText(""),
      autoSize: false,
      w: 240,
      scale: editor.getResizeScaleFactor(),
    },
  });
  if (editor.getShape(id)) {
    startEditingShapeWithRichText(editor, id);
    editor.focus();
  }
  return id;
}
